<template>
  <div
    class="modal fade"
    ref="modalEl"
    tabindex="-1"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
  >
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header bg-primary text-white">
          <h5 class="modal-title">
            <i :class="editMode ? 'fas fa-user-edit me-2' : 'fas fa-user-plus me-2'"></i>
            {{ editMode ? 'Editar Cliente' : 'Registrar Cliente' }}
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="close"></button>
        </div>
        <div class="modal-body" style="max-height: 75vh; overflow-y: auto;">
          <form novalidate @submit.prevent="submit">
            <div class="container-fluid">
              <div v-if="formError" class="alert alert-danger" role="alert">
                {{ formError }}
              </div>
              <!-- SPEC_FORMULARIO_CLIENTE_v0.1.md: identificación (obligatoria) →
                   contacto (opcional, recomendado) → más datos (opcional). -->

              <!-- 1. Identificación (obligatoria) -->
              <div class="section-header mb-3">
                <h6 class="text-primary border-bottom pb-2 mb-1">
                  <i class="fas fa-id-card me-2"></i>Identificación
                </h6>
                <small class="text-muted">Obligatorio. Con la cédula o el RUC se completa el nombre desde el padrón DNIT.</small>
              </div>

              <div class="row mb-3">
                <div class="col-md-4">
                  <label class="form-label fw-bold" for="cliTipoDoc">Tipo de documento <span class="text-danger">*</span></label>
                  <select id="cliTipoDoc" v-model="form.documento.tipo" class="form-select">
                    <option v-for="(t, codigo) in tiposDocumento" :key="codigo" :value="codigo">{{ t.nombre }}</option>
                  </select>
                </div>
                <div class="col-md-8">
                  <label class="form-label fw-bold" for="cliNroDoc">Número <span class="text-danger">*</span></label>
                  <input
                    id="cliNroDoc"
                    type="text"
                    :value="form.documento.numero"
                    class="form-control"
                    :class="{ 'is-invalid': validationErrors.documento }"
                    :placeholder="placeholderDocumento"
                    :inputmode="form.documento.tipo === 'CI' ? 'numeric' : 'text'"
                    maxlength="20"
                    required
                    @input="onDocumentoInput"
                  />
                  <div v-if="validationErrors.documento" class="invalid-feedback d-block">{{ validationErrors.documento }}</div>
                  <!-- RN-DN1 a RN-DN5 y RN-FC2: autocompletado con los clientes y el padrón -->
                  <div v-if="padron.consultando" class="form-text">Buscando en el padrón…</div>
                  <div v-else-if="padron.yaCliente" class="form-text text-warning-emphasis">{{ padron.yaCliente }}</div>
                  <template v-else>
                    <div v-if="form.documento.tipo === 'CI' && form.ruc_padron" class="form-text text-success fw-semibold">
                      Tiene RUC {{ form.ruc_padron }}: se le factura al RUC.
                    </div>
                    <div v-if="padron.aviso" class="form-text text-primary">{{ padron.aviso }}</div>
                  </template>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-8">
                  <label class="form-label fw-bold" for="cliNombre">Nombre o razón social <span class="text-danger">*</span></label>
                  <input id="cliNombre" type="text" v-model="form.nombre_completo" class="form-control" :class="{ 'is-invalid': validationErrors.nombre_completo }" maxlength="200" required />
                  <div v-if="validationErrors.nombre_completo" class="invalid-feedback d-block">Ingresá el nombre o la razón social.</div>
                </div>
                <div class="col-md-4">
                  <label class="form-label fw-bold" for="cliTipoPersona">Tipo de persona <span class="text-danger">*</span></label>
                  <select id="cliTipoPersona" v-model="form.identificacion.tipo_persona" class="form-select" @change="padron.tipoTocado = true">
                    <option value="fisica">Persona física</option>
                    <option value="juridica">Persona jurídica (empresa)</option>
                  </select>
                  <small class="text-muted">Se informa en la factura electrónica.</small>
                </div>
              </div>

              <!-- 2. Contacto (opcional, recomendado) -->
              <div class="section-header mb-3 mt-4">
                <h6 class="text-primary border-bottom pb-2 mb-1">
                  <i class="fas fa-phone me-2"></i>Contacto <span class="fw-normal text-muted small">(opcional, recomendado)</span>
                </h6>
                <small class="text-muted">Sirven para enviarle la factura y avisos por WhatsApp o correo.</small>
              </div>

              <div class="row mb-3">
                <div class="col-md-6">
                  <label class="form-label fw-bold" for="cliCelular">Celular</label>
                  <input
                    id="cliCelular"
                    type="tel"
                    inputmode="tel"
                    :value="form.telefono"
                    class="form-control"
                    :class="{ 'is-invalid': validationErrors.telefono }"
                    placeholder="0981123456 o +595981123456"
                    maxlength="16"
                    @input="onCelularInput"
                  />
                  <div v-if="validationErrors.telefono" class="invalid-feedback d-block">{{ validationErrors.telefono }}</div>
                  <small v-else class="text-muted">Sólo números, con + adelante si es internacional. Sin espacios ni guiones.</small>
                </div>
                <div class="col-md-6">
                  <label class="form-label fw-bold" for="cliCorreo">Correo</label>
                  <input id="cliCorreo" type="email" v-model.trim="form.email" class="form-control" :class="{ 'is-invalid': validationErrors.email }" placeholder="nombre@dominio.com" maxlength="100" />
                  <div v-if="validationErrors.email" class="invalid-feedback d-block">{{ validationErrors.email }}</div>
                </div>
              </div>

              <!-- 3. Más datos (opcional) -->
              <details class="mas-datos mt-4" :open="editMode">
                <summary class="text-primary fw-semibold mb-3">
                  <i class="fas fa-plus-circle me-2"></i>Más datos <span class="fw-normal text-muted small">(opcional)</span>
                </summary>

              <div class="row mb-3">
                <div class="col-md-4">
                  <label class="form-label fw-bold">Nombre de fantasía</label>
                  <input type="text" v-model="form.nombre_fantasia" class="form-control" />
                </div>
                <div class="col-md-5">
                  <label class="form-label fw-bold">Dirección</label>
                  <input type="text" v-model="form.direccion" class="form-control" />
                </div>
                <div class="col-md-3">
                  <label class="form-label fw-bold">Condiciones de pago</label>
                  <select v-model="form.condiciones_pago" class="form-select">
                    <option value="CONTADO">Contado</option>
                    <option value="CREDITO">Crédito</option>
                  </select>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-4">
                  <label class="form-label fw-bold">Nombres</label>
                  <input type="text" v-model="form.identificacion.nombres" class="form-control" />
                </div>
                <div class="col-md-4">
                  <label class="form-label fw-bold">Apellidos</label>
                  <input type="text" v-model="form.identificacion.apellidos" class="form-control" />
                </div>
                <div class="col-md-4">
                  <label class="form-label fw-bold">Razón social</label>
                  <input type="text" v-model="form.identificacion.razon_social" class="form-control" />
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-3">
                  <label class="form-label fw-bold">Fecha de nacimiento</label>
                  <input
                    type="text"
                    :value="form.identificacion.fecha_nacimiento"
                    class="form-control"
                    placeholder="dd/mm/aaaa"
                    inputmode="numeric"
                    maxlength="10"
                    @input="handleFechaNacimientoInput"
                  />
                </div>
                <div class="col-md-3">
                  <label class="form-label fw-bold">Género</label>
                  <select v-model="form.identificacion.genero_id" class="form-select">
                    <option value="1">Masculino</option>
                    <option value="2">Femenino</option>
                    <option value="3">Otro</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="form-label fw-bold">Estado civil</label>
                  <select v-model="form.identificacion.estado_relacion_id" class="form-select">
                    <option value="1">Soltero/a</option>
                    <option value="2">Casado/a</option>
                    <option value="3">Divorciado/a</option>
                    <option value="4">Viudo/a</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="form-label fw-bold">Etapa del cliente</label>
                  <select v-model="form.identificacion.etapa_cliente_id" class="form-select">
                    <option value="1">Prospecto</option>
                    <option value="2">Cliente nuevo</option>
                    <option value="3">Cliente regular</option>
                    <option value="4">Cliente VIP</option>
                  </select>
                </div>
              </div>

              <!-- Teléfonos Adicionales -->
              <div class="row mb-3">
                <div class="col-12">
                  <label class="form-label fw-bold">Teléfonos Adicionales</label>
                  <div class="border rounded p-3 bg-light">
                    <div v-for="(telefono, index) in form.contacto.telefonos" :key="index" class="row mb-2">
                      <div class="col-md-4">
                        <select v-model="telefono.tipo_contacto_id" class="form-select form-select-sm">
                          <option value="1">Teléfono Fijo</option>
                          <option value="2">Celular</option>
                          <option value="3">WhatsApp</option>
                        </select>
                      </div>
                      <div class="col-md-6">
                        <input type="tel" v-model="telefono.valor" class="form-control form-control-sm" placeholder="Número de teléfono" />
                      </div>
                      <div class="col-md-2">
                        <button type="button" class="btn btn-outline-danger btn-sm" @click="removeTelefono(index)">
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                    <button type="button" class="btn btn-outline-primary btn-sm" @click="addTelefono">
                      <i class="fas fa-plus me-1"></i>Agregar Teléfono
                    </button>
                  </div>
                </div>
              </div>

              <!-- Emails Adicionales -->
              <div class="row mb-3">
                <div class="col-12">
                  <label class="form-label fw-bold">Emails Adicionales</label>
                  <div class="border rounded p-3 bg-light">
                    <div v-for="(email, index) in form.contacto.emails" :key="index" class="row mb-2">
                      <div class="col-md-4">
                        <select v-model="email.tipo_contacto_id" class="form-select form-select-sm">
                          <option value="3">Email Personal</option>
                          <option value="4">Email Trabajo</option>
                          <option value="5">Email Facturación</option>
                        </select>
                      </div>
                      <div class="col-md-6">
                        <input type="email" v-model="email.valor" class="form-control form-control-sm" placeholder="Dirección de email" />
                      </div>
                      <div class="col-md-2">
                        <button type="button" class="btn btn-outline-danger btn-sm" @click="removeEmail(index)">
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                    <button type="button" class="btn btn-outline-primary btn-sm" @click="addEmail">
                      <i class="fas fa-plus me-1"></i>Agregar Email
                    </button>
                  </div>
                </div>
              </div>

              <!-- Redes Sociales -->
              <div class="row mb-3">
                <div class="col-12">
                  <label class="form-label fw-bold">Redes Sociales</label>
                  <div class="border rounded p-3 bg-light">
                    <div v-for="(red, index) in form.contacto.redes" :key="index" class="row mb-2">
                      <div class="col-md-3">
                        <select v-model="red.red_social_id" class="form-select form-select-sm">
                          <option value="1">Twitter</option>
                          <option value="2">Facebook</option>
                          <option value="3">Instagram</option>
                          <option value="4">LinkedIn</option>
                          <option value="5">TikTok</option>
                        </select>
                      </div>
                      <div class="col-md-3">
                        <input type="text" v-model="red.handle" class="form-control form-control-sm" placeholder="@usuario" />
                      </div>
                      <div class="col-md-4">
                        <input type="url" v-model="red.url" class="form-control form-control-sm" placeholder="URL completa" />
                      </div>
                      <div class="col-md-2">
                        <button type="button" class="btn btn-outline-danger btn-sm" @click="removeRed(index)">
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                    <button type="button" class="btn btn-outline-primary btn-sm" @click="addRed">
                      <i class="fas fa-plus me-1"></i>Agregar Red Social
                    </button>
                  </div>
                </div>
              </div>

              <!-- Direcciones Detalladas -->
              <div class="row mb-3">
                <div class="col-12">
                  <label class="form-label fw-bold">Direcciones Detalladas</label>
                  <div class="border rounded p-3 bg-light">
                    <div v-for="(direccion, index) in form.direcciones" :key="index" class="border rounded p-3 mb-3 bg-white">
                      <div class="row mb-2">
                        <div class="col-md-4">
                          <label class="form-label fw-bold">Tipo de Dirección</label>
                          <select v-model="direccion.tipo_direccion_id" class="form-select form-select-sm">
                            <option value="1">Domicilio</option>
                            <option value="2">Trabajo</option>
                            <option value="3">Comercial</option>
                            <option value="4">Facturación</option>
                          </select>
                        </div>
                        <div class="col-md-4">
                          <label class="form-label fw-bold">País</label>
                          <input type="text" v-model="direccion.pais" class="form-control form-control-sm" placeholder="Paraguay" />
                        </div>
                        <div class="col-md-4">
                          <label class="form-label fw-bold">Departamento</label>
                          <select v-model="direccion.departamento_id" class="form-select form-select-sm">
                            <option value="1">Concepción</option>
                            <option value="2">San Pedro</option>
                            <option value="3">Cordillera</option>
                            <option value="4">Guairá</option>
                            <option value="5">Central</option>
                            <option value="6">Caaguazú</option>
                            <option value="7">Caazapá</option>
                          </select>
                        </div>
                      </div>
                      <div class="row mb-2">
                        <div class="col-md-4">
                          <label class="form-label fw-bold">Ciudad</label>
                          <select v-model="direccion.ciudad_id" class="form-select form-select-sm">
                            <option value="101">Asunción</option>
                            <option value="102">San Lorenzo</option>
                            <option value="103">Lambaré</option>
                            <option value="104">Fernando de la Mora</option>
                            <option value="105">Luque</option>
                          </select>
                        </div>
                        <div class="col-md-4">
                          <label class="form-label fw-bold">Barrio</label>
                          <input type="text" v-model="direccion.barrio" class="form-control form-control-sm" placeholder="Nombre del barrio" />
                        </div>
                        <div class="col-md-4">
                          <label class="form-label fw-bold">Calle Principal</label>
                          <input type="text" v-model="direccion.calle_principal" class="form-control form-control-sm" placeholder="Nombre de la calle" />
                        </div>
                      </div>
                      <div class="row mb-2">
                        <div class="col-md-3">
                          <label class="form-label fw-bold">Número</label>
                          <input type="text" v-model="direccion.numero" class="form-control form-control-sm" placeholder="Nro." />
                        </div>
                        <div class="col-md-5">
                          <label class="form-label fw-bold">Referencia</label>
                          <input type="text" v-model="direccion.referencia" class="form-control form-control-sm" placeholder="Referencias adicionales" />
                        </div>
                        <div class="col-md-2">
                          <label class="form-label fw-bold">Latitud</label>
                          <input type="number" step="0.0001" v-model="direccion.lat" class="form-control form-control-sm" placeholder="-25.3001" />
                        </div>
                        <div class="col-md-2">
                          <label class="form-label fw-bold">Longitud</label>
                          <input type="number" step="0.0001" v-model="direccion.lng" class="form-control form-control-sm" placeholder="-57.6359" />
                        </div>
                      </div>
                      <div class="row">
                        <div class="col-md-10">
                          <div class="form-check">
                            <input class="form-check-input" type="checkbox" v-model="direccion.es_principal" :id="'principal-' + index">
                            <label class="form-check-label" :for="'principal-' + index">
                              Dirección principal
                            </label>
                          </div>
                        </div>
                        <div class="col-md-2 text-end">
                          <button type="button" class="btn btn-outline-danger btn-sm" @click="removeDireccion(index)">
                            <i class="fas fa-trash"></i> Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                    <button type="button" class="btn btn-outline-primary btn-sm" @click="addDireccion">
                      <i class="fas fa-plus me-1"></i>Agregar Dirección
                    </button>
                  </div>
                </div>
              </div>

              </details>

              <!-- Botones de acción: fuera de «Más datos» y fijos al pie, siempre
                   visibles aunque los datos opcionales estén plegados o abiertos. -->
              <div class="row mt-4 acciones-cliente">
                <div class="col-12">
                  <div class="d-flex justify-content-end gap-2">
                    <button type="button" class="btn btn-outline-secondary" @click="close">
                      <i class="fas fa-times me-1"></i>Cancelar
                    </button>
                    <button type="submit" class="btn btn-primary">
                      <i :class="editMode ? 'fas fa-edit me-1' : 'fas fa-save me-1'"></i>
                      {{ editMode ? 'Actualizar Cliente' : 'Guardar Cliente' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from 'bootstrap';
import apiService from '@/services/apiService.js';
import { documentoConsultable, aplicarPadron, avisoPadron } from '@/utils/padronDnit.mjs';
import { TIPOS, validarDocumento, documentoDesdeCliente, validarCelular, validarCorreo } from '@/utils/documentoCliente.mjs';

export default {
  name: 'RegistrarClienteModal',
  props: {
    open: { type: Boolean, default: false },
    prefillRuc: { type: String, default: '' },
    prefillDocumento: { type: String, default: '' },
    editMode: { type: Boolean, default: false },
    clienteData: { type: Object, default: () => null }
  },
  emits: ['saved', 'close'],
  data() {
    return {
      bsModal: null,
      tiposDocumento: TIPOS,
      documentoOriginal: null,
      // Autocompletado con el padrón DNIT (SPEC_ALTA_CLIENTE_DNIT_Y_RECEPTOR_v0.1.md)
      padron: { consultando: false, aviso: '', yaCliente: '', tipoTocado: false },
      formError: '',
      validationErrors: {},
      form: {
        // Campos originales (obligatorios)
        nombre_completo: '',
        // SPEC_FORMULARIO_CLIENTE_v0.1.md RN-FC1: un solo documento.
        documento: { tipo: 'CI', numero: '' },
        ruc_padron: null,
        direccion: '',
        telefono: '',
        email: '',
        nombre_fantasia: '',
        condiciones_pago: 'CONTADO',
        
        // Nuevos campos - Identificación
        identificacion: {
          tipo_persona: 'fisica',
          tipo_documento_id: 1,
          nombres: '',
          apellidos: '',
          razon_social: '',
          genero_id: 1,
          fecha_nacimiento: '',
          estado_relacion_id: 1,
          etapa_cliente_id: 1
        },
        
        // Nuevos campos - Contacto
        contacto: {
          telefonos: [],
          emails: [],
          redes: []
        },
        
        // Nuevos campos - Direcciones
        direcciones: []
      }
    };
  },
  watch: {
    open(val) {
      if (val) this.show(); else this.hide();
    },
    // RN-DN2: al tipear, cuando el operador deja de escribir (500 ms). Sólo en
    // el alta: en la edición el cliente ya existe.
    'form.documento.tipo'() {
      this.form.ruc_padron = null;
      this.validationErrors = { ...this.validationErrors, documento: null };
      this.programarConsultaPadron();
    },
    'form.documento.numero'() {
      this.programarConsultaPadron();
    }
  },
  computed: {
    placeholderDocumento() {
      return { CI: 'Ej.: 1234567 (sin dígito verificador)', RUC: 'Ej.: 80001324-7 (o sin el dígito)' }[this.form.documento?.tipo] || 'Letras y números, sin espacios';
    }
  },
  methods: {
    // RN-FC2/RN-FC3: sólo cédula y RUC se buscan en el padrón. En la edición
    // también, si el operador cambia el número.
    programarConsultaPadron() {
      const valor = this.form.documento?.numero;
      if (this.editMode && valor === this.documentoOriginal) return;
      clearTimeout(this.padronTimer);
      if (this.padronAbort) this.padronAbort.abort();
      this.padron.aviso = '';
      this.padron.yaCliente = '';
      this.form.ruc_padron = null;
      if (!['CI', 'RUC'].includes(this.form.documento?.tipo) || !documentoConsultable(valor)) return;
      this.padronTimer = setTimeout(() => this.consultarPadron(valor), 500);
    },
    // RN-DN1 a RN-DN5. Nunca molesta: si el padrón no responde, el alta sigue
    // manual sin ningún mensaje de error.
    async consultarPadron(documento) {
      const controlador = new AbortController();
      this.padronAbort = controlador;
      this.padron.consultando = true;
      try {
        const { data } = await apiService.get(`/api/clients/padron/${encodeURIComponent(documento)}`, {}, { signal: controlador.signal });
        if (controlador.signal.aborted || documento !== this.form.documento?.numero) return;
        const r = data?.data || {};
        if (r.origen === 'cliente') {
          this.padron.yaCliente = `Ya es cliente: ${r.cliente?.nombre_completo || 'sin nombre'}. Buscalo en la lista en vez de darlo de alta de nuevo.`;
        } else if (r.origen === 'padron' && r.cliente) {
          this.form = aplicarPadron(this.form, r.cliente, { tipoTocado: this.padron.tipoTocado });
          this.padron.aviso = avisoPadron(r.cliente);
        }
      } catch (error) {
        // 404 = no está en ningún lado: alta manual normal. Cualquier otra cosa, silencio (RN-DN5).
      } finally {
        if (this.padronAbort === controlador) this.padron.consultando = false;
      }
    },
    normalizeDateDigits(value) {
      return String(value || '').replace(/\D/g, '').slice(0, 8);
    },
    applyDateMask(value) {
      const digits = this.normalizeDateDigits(value);
      if (digits.length <= 2) return digits;
      if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    },
    formatDateForDisplay(value) {
      if (!value) return '';
      const raw = String(value).trim();
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        const [year, month, day] = raw.split('-');
        return `${day}/${month}/${year}`;
      }
      const date = new Date(raw);
      if (Number.isNaN(date.getTime())) return this.applyDateMask(raw);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },
    convertDisplayDateToISO(value) {
      const formatted = this.formatDateForDisplay(value);
      if (!/^\d{2}\/\d{2}\/\d{4}$/.test(formatted)) return '';
      const [day, month, year] = formatted.split('/');
      return `${year}-${month}-${day}`;
    },
    handleFechaNacimientoInput(event) {
      this.form.identificacion.fecha_nacimiento = this.applyDateMask(event.target.value);
    },
    show() {
      this.formError = '';
      this.validationErrors = {};
      if (!this.bsModal) {
        this.bsModal = new Modal(this.$refs.modalEl, {
          backdrop: 'static',
          keyboard: false
        });
      }
      
      // Cada apertura arranca sin aviso del padrón. En la edición, el tipo de
      // persona guardado manda: no se sugiere encima.
      this.padron = { consultando: false, aviso: '', yaCliente: '', tipoTocado: Boolean(this.editMode) };
      if (this.editMode && this.clienteData) {
        // Modo edición: precargar datos del cliente
        this.form = {
          // Campos originales (obligatorios) - precargados
          nombre_completo: this.clienteData.nombre_completo || '',
          // RN-FC11: tipo y número deducidos de lo guardado (formato viejo o nuevo).
          documento: (({ tipo, numero }) => ({ tipo, numero }))(documentoDesdeCliente(this.clienteData)),
          ruc_padron: documentoDesdeCliente(this.clienteData).tipo === 'CI' ? documentoDesdeCliente(this.clienteData).ruc : null,
          direccion: this.clienteData.direccion || '',
          telefono: this.clienteData.telefono || '',
          email: this.clienteData.email || '',
          nombre_fantasia: this.clienteData.nombre_fantasia || '',
          condiciones_pago: this.clienteData.condiciones_pago || 'CONTADO',
          
          // Nuevos campos - Identificación (con valores por defecto si no existen)
          identificacion: {
            tipo_persona: this.clienteData.identificacion?.tipo_persona || this.clienteData.tipo_persona || 'fisica',
            tipo_documento_id: this.clienteData.identificacion?.tipo_documento_id || 1,
            nombres: this.clienteData.identificacion?.nombres || this.clienteData.nombres || '',
            apellidos: this.clienteData.identificacion?.apellidos || this.clienteData.apellidos || '',
            razon_social: this.clienteData.identificacion?.razon_social || this.clienteData.razon_social || '',
            genero_id: this.clienteData.identificacion?.genero_id || 1,
            fecha_nacimiento: this.formatDateForDisplay(this.clienteData.identificacion?.fecha_nacimiento || ''),
            estado_relacion_id: this.clienteData.identificacion?.estado_relacion_id || 1,
            etapa_cliente_id: this.clienteData.identificacion?.etapa_cliente_id || 1
          },
          
          // Nuevos campos - Contacto (con arrays por defecto si no existen)
          contacto: {
            telefonos: this.clienteData.contacto?.telefonos || [],
            emails: this.clienteData.contacto?.emails || [],
            redes: this.clienteData.contacto?.redes || []
          },
          
          // Nuevos campos - Direcciones (con array por defecto si no existe)
          direcciones: this.clienteData.direcciones || []
        };
        this.documentoOriginal = this.form.documento.numero;
      } else {
        // Modo creación: formulario vacío con prefill básico
        this.documentoOriginal = null;
        this.form = {
          // Campos originales (obligatorios)
          nombre_completo: '',
          // Lo que se tipeó en la búsqueda: con guion es un RUC, si no, una cédula.
          documento: (() => {
            const pre = String(this.prefillRuc || this.prefillDocumento || '').trim();
            return { tipo: pre.includes('-') || pre.replace(/\D/g, '').length > 8 ? 'RUC' : 'CI', numero: pre };
          })(),
          ruc_padron: null,
          direccion: '',
          telefono: '',
          email: '',
          nombre_fantasia: '',
          condiciones_pago: 'CONTADO',
          
          // Nuevos campos - Identificación
          identificacion: {
            tipo_persona: 'fisica',
            tipo_documento_id: 1,
            nombres: '',
            apellidos: '',
            razon_social: '',
            genero_id: 1,
            fecha_nacimiento: '',
            estado_relacion_id: 1,
            etapa_cliente_id: 1
          },
          
          // Nuevos campos - Contacto
          contacto: {
            telefonos: [],
            emails: [],
            redes: []
          },
          
          // Nuevos campos - Direcciones
          direcciones: []
        };
      }
      
      this.bsModal.show();
    },
    hide() {
      this.bsModal?.hide();
    },
    close() {
      this.formError = '';
      this.validationErrors = {};
      this.hide();
      this.$emit('close');
    },
    resolveErrorMessage(error, fallback) {
      return error?.userMessage
        || error?.normalized?.message
        || error?.response?.data?.message
        || error?.response?.data?.error
        || fallback;
    },
    notifyError(message) {
      this.formError = message;
      if (window.alert?.error) {
        window.alert.error(message);
      } else {
        window.alert(message);
      }
    },
    // SPEC_FORMULARIO_CLIENTE_v0.1.md: mismas reglas que el API (documentoCliente).
    validateClientForm() {
      const errors = {};
      const doc = validarDocumento(this.form.documento);
      if (!doc.ok) errors.documento = doc.error;
      if (!String(this.form.nombre_completo || '').trim()) errors.nombre_completo = true;
      const celular = validarCelular(this.form.telefono);
      if (!celular.ok) errors.telefono = celular.error;
      const correo = validarCorreo(this.form.email);
      if (!correo.ok) errors.email = correo.error;

      this.validationErrors = errors;
      if (!Object.keys(errors).length) {
        this.formError = '';
        if (doc.numero !== this.form.documento.numero) this.form.documento.numero = doc.numero; // RUC con el DV completado
        return true;
      }
      this.notifyError('Revisá los campos marcados en rojo.');
      return false;
    },
    // RN-FC2 a RN-FC4: el número se filtra mientras se tipea.
    onDocumentoInput(event) {
      const tipo = this.form.documento.tipo;
      let v = String(event.target.value || '');
      if (tipo === 'CI') v = v.replace(/\D/g, '');
      else if (tipo === 'RUC') v = v.replace(/[^\d-]/g, '').replace(/(-.*)-/g, '$1');
      else v = v.toUpperCase().replace(/[^A-Z0-9-]/g, '');
      this.form.documento.numero = v;
      event.target.value = v;
    },
    // RN-FC7: un + opcional al inicio y sólo dígitos.
    onCelularInput(event) {
      const crudo = String(event.target.value || '');
      const v = (crudo.trim().startsWith('+') ? '+' : '') + crudo.replace(/\D/g, '');
      this.form.telefono = v;
      event.target.value = v;
    },
    async submit() {
      if (!this.validateClientForm()) return;

      try {
        let response;
        
        if (this.editMode && this.clienteData) {
          // Modo edición: actualizar cliente existente
          const clienteId = this.clienteData.cliente_id || this.clienteData.id;
          
          // Preparar el body solo con los campos básicos requeridos
          const body = {
            nombre_completo: this.form.nombre_completo,
            // RN-FC10: el API arma ci / ruc / nro_documento con una sola regla.
            documento: this.form.documento,
            ruc_padron: this.form.ruc_padron,
            direccion: this.form.direccion,
            telefono: this.form.telefono,
            email: this.form.email,
            nombre_fantasia: this.form.nombre_fantasia,
            condiciones_pago: this.form.condiciones_pago,
            // RN-RF1: antes la edición no mandaba el tipo de persona.
            tipo_persona: this.form.identificacion?.tipo_persona || null,
            nombres: this.form.identificacion?.nombres || null,
            apellidos: this.form.identificacion?.apellidos || null,
            razon_social: this.form.identificacion?.razon_social || null
          };
          
          response = await apiService.put(
            `${process.env.VUE_APP_API_BASE_URL}/api/clients/${clienteId}`, 
            body
          );
        } else {
          // Modo creación: crear nuevo cliente (envía todo el form)
          const body = {
            ...this.form,
            tipo_persona: this.form.identificacion?.tipo_persona || null,
            identificacion: {
              ...this.form.identificacion,
              fecha_nacimiento: this.convertDisplayDateToISO(this.form.identificacion.fecha_nacimiento)
            }
          };
          response = await apiService.post(`${process.env.VUE_APP_API_BASE_URL}/api/clients`, body);
        }
        
        this.$emit('saved', response.data?.cliente || response.data);
        this.close();
      } catch (e) {
        console.error(`Error al ${this.editMode ? 'actualizar' : 'guardar'} el cliente:`, e);
        this.notifyError(this.resolveErrorMessage(e, `No se pudo ${this.editMode ? 'actualizar' : 'guardar'} el cliente.`));
      }
    },

    // Métodos para manejar teléfonos
    addTelefono() {
      this.form.contacto.telefonos.push({
        contacto_id: null,
        tipo_contacto_id: 1,
        valor: '',
        es_principal: false
      });
    },
    removeTelefono(index) {
      this.form.contacto.telefonos.splice(index, 1);
    },

    // Métodos para manejar emails
    addEmail() {
      this.form.contacto.emails.push({
        contacto_id: null,
        tipo_contacto_id: 3,
        valor: '',
        es_principal: false
      });
    },
    removeEmail(index) {
      this.form.contacto.emails.splice(index, 1);
    },

    // Métodos para manejar redes sociales
    addRed() {
      this.form.contacto.redes.push({
        cliente_red_id: null,
        red_social_id: 1,
        handle: '',
        url: '',
        es_principal: false
      });
    },
    removeRed(index) {
      this.form.contacto.redes.splice(index, 1);
    },

    // Métodos para manejar direcciones
    addDireccion() {
      this.form.direcciones.push({
        direccion_id: null,
        tipo_direccion_id: 1,
        pais: 'Paraguay',
        departamento_id: 5,
        ciudad_id: 101,
        barrio: '',
        calle_principal: '',
        numero: '',
        referencia: '',
        lat: null,
        lng: null,
        es_principal: false
      });
    },
    removeDireccion(index) {
      this.form.direcciones.splice(index, 1);
    }
  },
  mounted() {
    this.bsModal = new Modal(this.$refs.modalEl);
    if (this.open) this.show();
  },
  beforeUnmount() {
    this.bsModal?.hide();
    this.bsModal = null;
    clearTimeout(this.padronTimer);
    if (this.padronAbort) this.padronAbort.abort();
  }
};
</script>

<style scoped>
.acciones-cliente {
  position: sticky;
  bottom: -1rem;
  background: var(--bs-body-bg, #fff);
  border-top: 1px solid var(--bs-border-color, #dee2e6);
  padding: 0.75rem 0;
  z-index: 2;
}

.modal-xl {
  max-width: 1200px;
}

.section-header {
  margin-top: 1.5rem;
}

.section-header:first-of-type {
  margin-top: 0;
}

.section-header h6 {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.form-label.fw-bold {
  font-size: 0.9rem;
  color: #333;
}

.text-danger {
  color: #dc3545 !important;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.border {
  border: 1px solid #dee2e6 !important;
}

.rounded {
  border-radius: 0.375rem !important;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.form-control-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.form-select-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.gap-2 {
  gap: 0.5rem !important;
}

.modal-body {
  padding: 1.5rem;
}

.btn-close-white {
  filter: invert(1) grayscale(100%) brightness(200%);
}

/* Mejoras en el scroll */
.modal-body::-webkit-scrollbar {
  width: 8px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Efectos hover para botones */
.btn:hover {
  transform: translateY(-1px);
  transition: all 0.2s ease-in-out;
}

/* Mejora visual para las secciones */
.bg-white {
  background-color: #ffffff !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .modal-xl {
    max-width: 95%;
    margin: 1rem auto;
  }
  
  .modal-body {
    padding: 1rem;
  }
}
</style>
